import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  Archive,
  Barcode,
  Images,
  Loader2,
  Pencil,
  Plus,
  Search,
  Store,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePermission } from '@/features/auth/auth.store';
import { useBusinesses } from '@/features/business/use-businesses';
import { TAX_RULES } from '@/features/item-prices/item-prices.api';
import { cn } from '@/lib/utils';
import { GtinsSheet } from './gtins-sheet';
import { MediaSheet } from './media-sheet';
import {
  NEW_REGISTRATION_ITEM,
  useApplyMedia,
  useArchiveRegistrationItem,
  useAssignItem,
  useRegistrationSearch,
  useSaveRegistrationItem,
  type AssignInput,
  type RegistrationItem,
} from './item-registration.api';

/**
 * Item registration — the shared library a store's catalogue is assigned from.
 *
 * Two jobs, and the row says which one it is waiting for: an unassigned item
 * offers **Assign**, an assigned one offers to push its pictures down to the
 * stores that took it. Editing the shared description is a third, and only a
 * SuperAdmin does it.
 */

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <Label className="text-sm font-normal">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/** The description fields both the editor and the assign drawer show. */
function DescriptionFields({
  value,
  onChange,
  readOnly,
}: {
  value: Partial<RegistrationItem>;
  onChange: (patch: Partial<RegistrationItem>) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();

  const text = (key: keyof RegistrationItem) => ({
    value: (value[key] as string | null) ?? '',
    disabled: readOnly,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({ [key]: event.target.value } as Partial<RegistrationItem>),
  });

  const number = (key: keyof RegistrationItem) => ({
    type: 'number' as const,
    value: (value[key] as number | null) ?? '',
    disabled: readOnly,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange({
        [key]: event.target.value === '' ? null : Number(event.target.value),
      } as Partial<RegistrationItem>),
  });

  return (
    <div className="space-y-4">
      <Field label={t('itemRegistration.brand')}>
        <Input {...text('brand')} />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`${t('itemRegistration.name')} (EN)`}>
          <Input {...text('name')} />
        </Field>
        <Field label={`${t('itemRegistration.name')} (ES)`}>
          <Input {...text('nameSpa')} />
        </Field>
        <Field label={`${t('itemRegistration.description')} (EN)`}>
          <Input {...text('description')} />
        </Field>
        <Field label={`${t('itemRegistration.description')} (ES)`}>
          <Input {...text('descriptionSpa')} />
        </Field>
      </div>

      <Field
        label={t('itemRegistration.searchTags')}
        hint={t('itemRegistration.searchTagsHint')}
      >
        <Textarea
          rows={2}
          value={value.searchTags ?? ''}
          disabled={readOnly}
          onChange={(event) => onChange({ searchTags: event.target.value })}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t('itemRegistration.division')}>
          <Input {...text('suggestedDivision')} />
        </Field>
        <Field label={t('itemRegistration.group')}>
          <Input {...text('suggestedGroup')} />
        </Field>
        <Field label={t('itemRegistration.suggestedCategory')}>
          <Input {...text('suggestedCategoryName')} />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('itemRegistration.pack')}>
          <Input {...number('pack')} />
        </Field>
        <Field label={t('itemRegistration.taxRule')}>
          <Select
            value={String(value.taxRule ?? 3)}
            disabled={readOnly}
            onValueChange={(next) => onChange({ taxRule: Number(next) })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAX_RULES.map((rule) => (
                <SelectItem key={rule.value} value={String(rule.value)}>
                  {t(rule.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={`${t('itemRegistration.stateTax')} %`}>
          <Input step="0.001" {...number('stateTaxPct')} />
        </Field>
        <Field label={`${t('itemRegistration.municipalTax')} %`}>
          <Input step="0.001" {...number('munTaxPct')} />
        </Field>
      </div>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <Toggle
          label={t('itemRegistration.packagingSensitive')}
          checked={!!value.isPackagingSensitive}
          onChange={(isPackagingSensitive) => onChange({ isPackagingSensitive })}
        />
        <Toggle
          label={t('itemRegistration.temperatureSensitive')}
          checked={!!value.isTemperatureSensitive}
          onChange={(isTemperatureSensitive) =>
            onChange({ isTemperatureSensitive })
          }
        />
        <Toggle
          label={t('itemRegistration.alcoholicBeverage')}
          checked={!!value.isAlcoholicBeverage}
          onChange={(isAlcoholicBeverage) => onChange({ isAlcoholicBeverage })}
        />
        <Toggle
          label={t('itemRegistration.perishable')}
          checked={!!value.isPerishable}
          onChange={(isPerishable) => onChange({ isPerishable })}
        />
        <Toggle
          label={t('itemRegistration.isEbt')}
          checked={!!value.isEbt}
          onChange={(isEbt) => onChange({ isEbt })}
        />
        <Toggle
          label={t('itemRegistration.idRequired')}
          checked={!!value.idRequired}
          onChange={(idRequired) => onChange({ idRequired })}
        />
        <Toggle
          label={t('itemRegistration.hasPluCode')}
          checked={!!value.hasPluCode}
          onChange={(hasPluCode) => onChange({ hasPluCode })}
        />
        <Toggle
          label={t('itemRegistration.hasRandomWeight')}
          checked={!!value.hasRandomWeight}
          onChange={(hasRandomWeight) => onChange({ hasRandomWeight })}
        />
      </div>
    </div>
  );
}

export function ItemRegistrationPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const isAdmin = usePermission('isSuperAdmin');

  const { data: businesses } = useBusinesses();

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [editing, setEditing] = useState<Partial<RegistrationItem> | null>(null);
  const [assigning, setAssigning] = useState<AssignInput | null>(null);
  const [multiStore, setMultiStore] = useState(false);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [gtinsFor, setGtinsFor] = useState<RegistrationItem | null>(null);
  const [mediaFor, setMediaFor] = useState<RegistrationItem | null>(null);
  const [applyMediaFor, setApplyMediaFor] = useState<RegistrationItem | null>(
    null
  );

  const model = useMemo(
    () => ({ searchTerm: query, page, size }),
    [query, page, size]
  );

  const search = useRegistrationSearch(model);
  const save = useSaveRegistrationItem();
  const archive = useArchiveRegistrationItem();
  const assign = useAssignItem();
  const applyMedia = useApplyMedia();

  const rows = search.data?.items ?? [];
  const total = search.data?.total ?? 0;

  const label = (item: Partial<RegistrationItem>) =>
    [item.brand, isEnglish ? item.name : item.nameSpa]
      .filter(Boolean)
      .join(' ') || '—';

  const failed = () => toast.error(t('itemRegistration.actionFailed'));

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('itemRegistration.title')}
        description={t('itemRegistration.subtitle')}
        actions={
          isAdmin && (
            <Button
              size="sm"
              onClick={() => setEditing({ ...NEW_REGISTRATION_ITEM })}
            >
              <Plus />
              <span className="hidden sm:inline">
                {t('itemRegistration.createNew')}
              </span>
            </Button>
          )
        }
      />

      <div className="space-y-2">
        <form
          role="search"
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft === query && page === 0) void search.refetch();
            setQuery(draft);
            setPage(0);
          }}
        >
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('itemRegistration.searchPlaceholder')}
              aria-label={t('itemRegistration.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
          <Button type="submit" className="h-10" disabled={search.isFetching}>
            <Search className="sm:hidden" />
            <span className="hidden sm:inline">{t('common.search')}</span>
          </Button>
        </form>

        {search.isSuccess && (
          <p className="text-muted-foreground text-sm">
            {t('itemRegistration.showingOf', { shown: rows.length, total })}
          </p>
        )}
      </div>

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('itemRegistration.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void search.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-16 shrink-0 rounded" />
              <Skeleton className="h-4 w-56" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <div className="rounded-xl border border-dashed py-14 text-center">
            <p className="text-sm font-medium">
              {t('itemRegistration.itemNotFound')}
            </p>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('itemRegistration.itemNotFoundBody')}
            </p>
            {isAdmin && (
              <Button
                className="mt-4"
                onClick={() => setEditing({ ...NEW_REGISTRATION_ITEM })}
              >
                <Plus />
                {t('itemRegistration.createNew')}
              </Button>
            )}
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="relative rounded-xl">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'divide-y rounded-xl border',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center"
                >
                  {item.itemImage ? (
                    <img
                      src={item.itemImage}
                      alt=""
                      className="bg-muted size-16 shrink-0 rounded object-contain"
                    />
                  ) : (
                    <span className="bg-muted size-16 shrink-0 rounded" />
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{label(item)}</p>
                    <p className="text-muted-foreground text-xs">
                      {isEnglish ? item.description : item.descriptionSpa}
                    </p>
                    <p className="text-muted-foreground font-mono text-xs">
                      {item.gtin}
                    </p>
                    <div className="mt-1">
                      {item.isAlreadyAssigned ? (
                        <Badge variant="success">
                          {t('itemRegistration.assigned')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {t('itemRegistration.notAssigned')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2">
                    {item.isAlreadyAssigned ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setApplyMediaFor(item)}
                      >
                        <Images className="size-3.5" />
                        {t('itemRegistration.applyImages')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          setAssigning({ ...item, cost: 0, price: 0 });
                          setMultiStore(false);
                          setStoreIds([]);
                        }}
                      >
                        <Store className="size-3.5" />
                        {t('itemRegistration.assign')}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(item)}
                      >
                        <Pencil className="size-3.5" />
                        {t('itemRegistration.edit')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      {/* --------------------------------------------- describe an item */}
      <Sheet
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
        >
          <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
            <SheetTitle>
              {editing?.id
                ? t('itemRegistration.editItem')
                : t('itemRegistration.newItem')}
            </SheetTitle>
            {editing?.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={archive.isPending}
                  >
                    <Archive className="size-3.5" />
                    {t('itemRegistration.archive')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('itemRegistration.archive')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('itemRegistration.archivePrompt')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        archive.mutate(editing!.id!, {
                          onSuccess: () => {
                            toast.success(t('itemRegistration.archived'));
                            setEditing(null);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </SheetHeader>

          {editing && (
            <div className="space-y-4 p-4">
              {/* Barcodes and pictures belong to a saved item, so they are
                  offered once it has an id rather than as dead buttons. */}
              {editing.id && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setGtinsFor(editing as RegistrationItem)}
                  >
                    <Barcode className="size-3.5" />
                    {t('itemRegistration.gtins')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMediaFor(editing as RegistrationItem)}
                  >
                    <Images className="size-3.5" />
                    {t('itemRegistration.media')}
                  </Button>
                </div>
              )}

              <Field label="GTIN" hint={t('itemRegistration.primaryGtinHint')}>
                <Input
                  className="font-mono"
                  value={editing.gtin ?? ''}
                  onChange={(event) =>
                    setEditing({ ...editing, gtin: event.target.value })
                  }
                />
              </Field>

              <DescriptionFields
                value={editing}
                onChange={(patch) => setEditing({ ...editing, ...patch })}
              />

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={save.isPending}
                  onClick={() => setEditing(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={save.isPending || !editing.name?.trim()}
                  onClick={() =>
                    save.mutate(editing, {
                      onSuccess: (saved) => {
                        toast.success(t('itemRegistration.saved'));
                        // Create answers with the row and its new id, so the
                        // drawer stays open and the barcodes can be added
                        // next. Update answers with nothing.
                        setEditing(editing.id ? null : (saved ?? null));
                      },
                      onError: failed,
                    })
                  }
                >
                  {save.isPending && <Loader2 className="animate-spin" />}
                  {editing.id ? t('common.save') : t('itemRegistration.create')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* -------------------------------------------------- assign it */}
      <Sheet
        open={assigning !== null}
        onOpenChange={(next) => {
          if (!next) setAssigning(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>{t('itemRegistration.assignToCatalog')}</SheetTitle>
          </SheetHeader>

          {assigning && (
            <div className="space-y-4 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('itemRegistration.cost')}>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={assigning.cost ?? ''}
                    onChange={(event) =>
                      setAssigning({
                        ...assigning,
                        cost: Number(event.target.value),
                      })
                    }
                  />
                </Field>
                <Field label={t('itemRegistration.price')}>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={assigning.price ?? ''}
                    onChange={(event) =>
                      setAssigning({
                        ...assigning,
                        price: Number(event.target.value),
                      })
                    }
                  />
                </Field>
              </div>

              <div className="space-y-2 rounded-xl border p-3">
                <Toggle
                  label={t('itemRegistration.assignToMultipleStores')}
                  checked={multiStore}
                  onChange={(next) => {
                    setMultiStore(next);
                    if (!next) setStoreIds([]);
                  }}
                />

                {multiStore && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-xs font-semibold">
                        {t('itemRegistration.stores')}
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setStoreIds(
                            storeIds.length === (businesses ?? []).length
                              ? []
                              : (businesses ?? []).map((b) => b.id)
                          )
                        }
                      >
                        {storeIds.length === (businesses ?? []).length
                          ? t('itemRegistration.clearAll')
                          : t('itemRegistration.selectAll')}
                      </Button>
                    </div>
                    <div className="max-h-56 divide-y overflow-y-auto rounded-lg border">
                      {(businesses ?? []).map((business) => (
                        <label
                          key={business.id}
                          className="hover:bg-accent flex cursor-pointer items-center gap-2.5 p-2.5 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="size-4"
                            checked={storeIds.includes(business.id)}
                            onChange={() =>
                              setStoreIds((current) =>
                                current.includes(business.id)
                                  ? current.filter((id) => id !== business.id)
                                  : [...current, business.id]
                              )
                            }
                          />
                          {business.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* The shared description, editable here because the copy that
                  lands in the catalogue is what is being written. */}
              <DescriptionFields
                value={assigning}
                onChange={(patch) => setAssigning({ ...assigning, ...patch })}
              />

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={assign.isPending}
                  onClick={() => setAssigning(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={
                    assign.isPending ||
                    !assigning.gtin ||
                    !((assigning.price ?? 0) > 0) ||
                    !((assigning.cost ?? 0) > 0) ||
                    (multiStore && storeIds.length === 0)
                  }
                  onClick={() =>
                    assign.mutate(
                      {
                        item: assigning,
                        businessIds: multiStore ? storeIds : [],
                      },
                      {
                        onSuccess: () => {
                          toast.success(t('itemRegistration.assignedToast'));
                          setAssigning(null);
                        },
                        onError: failed,
                      }
                    )
                  }
                >
                  {assign.isPending && <Loader2 className="animate-spin" />}
                  {t('itemRegistration.submit')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ------------------------------------------- apply images down */}
      <AlertDialog
        open={applyMediaFor !== null}
        onOpenChange={(next) => {
          if (!next) setApplyMediaFor(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('itemRegistration.applyImages')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('itemRegistration.applyImagesBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Add and replace are two different acts, so they are two buttons
              rather than the original's confirm/cancel pair, where "cancel"
              silently meant replace. */}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button
              variant="outline"
              disabled={applyMedia.isPending}
              onClick={() =>
                applyMedia.mutate(
                  { id: applyMediaFor!.id, replace: false },
                  {
                    onSuccess: () => {
                      toast.success(t('itemRegistration.imagesApplied'));
                      setApplyMediaFor(null);
                    },
                    onError: failed,
                  }
                )
              }
            >
              {t('itemRegistration.addImages')}
            </Button>
            <Button
              disabled={applyMedia.isPending}
              onClick={() =>
                applyMedia.mutate(
                  { id: applyMediaFor!.id, replace: true },
                  {
                    onSuccess: () => {
                      toast.success(t('itemRegistration.imagesApplied'));
                      setApplyMediaFor(null);
                    },
                    onError: failed,
                  }
                )
              }
            >
              {t('itemRegistration.replaceImages')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GtinsSheet
        itemId={gtinsFor?.id ?? null}
        title={gtinsFor ? label(gtinsFor) : t('itemRegistration.gtins')}
        open={gtinsFor !== null}
        onOpenChange={(next) => {
          if (!next) setGtinsFor(null);
        }}
      />

      <MediaSheet
        itemId={mediaFor?.id ?? null}
        title={mediaFor ? label(mediaFor) : t('itemRegistration.media')}
        open={mediaFor !== null}
        onOpenChange={(next) => {
          if (!next) setMediaFor(null);
        }}
      />
    </div>
  );
}
